import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist, getWishlistItemId } = useWishlist();
  const { isAuthenticated, openLoginModal } = useAuth();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isTogglingWishlist, setIsTogglingWishlist] = useState(false);

  const ratingSummary = product.rating_summary || 0;
  const reviewCount = product.review_count || 0;
  const starRating = Math.round(ratingSummary / 20); // Convert 0-100 to 0-5
  const isInStock = product.stock_status === 'IN_STOCK';
  const inWishlist = isInWishlist(product.sku);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (!isInStock) return;
    setIsAddingToCart(true);
    try {
      await addToCart(product.sku, 1);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    setIsTogglingWishlist(true);
    try {
      if (inWishlist) {
        const wishlistItemId = getWishlistItemId(product.sku);
        if (wishlistItemId) {
          await removeFromWishlist(wishlistItemId);
        }
      } else {
        await addToWishlist(product.sku, 1);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    } finally {
      setIsTogglingWishlist(false);
    }
  };

  return (
    <div className="product-card">
      <button
        className={`wishlist-btn ${inWishlist ? 'in-wishlist' : ''}`}
        onClick={handleToggleWishlist}
        disabled={isTogglingWishlist}
        title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={inWishlist ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </button>
      <Link
        to={`/${product.url_key}${product.url_suffix || ''}`}
        className="product-card-link"
      >
        <img
          src={product.small_image?.url || '/placeholder.jpg'}
          alt={product.name}
          className="product-image"
        />
        <div className="product-info">
          <h3 className="product-title">{product.name}</h3>

          {reviewCount > 0 && (
            <div className="product-rating">
              <div className="product-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={star <= starRating ? 'star-filled' : 'star-empty'}
                  >
                    ★
                  </span>
                ))}
              </div>
              <span className="review-count">({reviewCount})</span>
            </div>
          )}

          <p className="product-price">
            ${product.price_range?.minimum_price?.final_price?.value || 'N/A'}
          </p>
        </div>
      </Link>
      <button
        className="add-to-cart-btn"
        onClick={handleAddToCart}
        disabled={isAddingToCart || !isInStock}
      >
        {isAddingToCart ? 'Adding...' : 'Add to Cart'}
      </button>
    </div>
  );
};

export default ProductCard;
